import { updateSpecificSkillCertificateApi } from "@/api/services/global/master";
import {
  TGetSpecificSkillCertificateListOutputDto,
  TGetSpecificSkillCertificateCompanySpecificIndustryOutputDto,
} from "@/api/services/global/master/dtos/specificSkillCertificate/get-master-specific-skill-certificate-list.dto";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Select } from "@/components/elements/Select";
import { Textarea } from "@/components/elements/Textarea";
import { Modal } from "@/components/elements/Modal";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { EMPTY_STR } from "@/constants/string";
import { CertificateIndustryRelationFormTable } from "@/features/master-data/components/SpecificSkillCertificate/CertificateIndustryRelationFormTable";
import { masterDataUpdateSpecificSkillCertificateSchema } from "@/features/master-data/schema/masterDataSpecificSkillCertificateSchema";
import { TMasterSpecificSkillCertificateUpdateForm } from "@/features/master-data/types/masterDataSpecificSkillCertificateForm";
import { setErrorForm, showError } from "@/utils/error";
import { toNumber } from "@/utils/number";
import { toast } from "@/utils/toast";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormLabel, Grid, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useState } from "react";
import { useForm } from "react-hook-form";
import { ESpecificSkillCertificateType, SPECIFIC_SKILL_CERTIFICATE_TYPE } from "@/types/enum";
import { objectToOption } from "@/utils/object";

type TUpdateSpecificSkillCertificateFormModalProps = {
  onClose: () => void;
  isOpen: boolean;
  selectedSpecificSkillCertificate: TGetSpecificSkillCertificateListOutputDto;
};

export const UpdateSpecificSkillCertificateFormModal: FC<TUpdateSpecificSkillCertificateFormModalProps> = ({
  onClose,
  isOpen,
  selectedSpecificSkillCertificate,
}) => {
  const queryClient = useQueryClient();
  const [selectedCertificateIndustryRelationRows, setSelectedCertificateIndustryRelationRows] = useState<
    TGetSpecificSkillCertificateCompanySpecificIndustryOutputDto[]
  >(selectedSpecificSkillCertificate?.specificSkillCertificateCompanySpecificIndustries ?? []);

  const methods = useForm<TMasterSpecificSkillCertificateUpdateForm>({
    resolver: yupResolver(masterDataUpdateSpecificSkillCertificateSchema),
    mode: "onBlur",
    defaultValues: {
      certificateName: selectedSpecificSkillCertificate?.certificateName || "",
      certificateType: selectedSpecificSkillCertificate?.certificateType || ESpecificSkillCertificateType.SPECIFIED_SKILL_NO1,
      description: selectedSpecificSkillCertificate?.description || "",
      specificSkillCertificateCompanySpecificIndustries: selectedSpecificSkillCertificate?.specificSkillCertificateCompanySpecificIndustries || [],
    },
  });

  const { control } = methods;

  const onSubmit = async (data: TMasterSpecificSkillCertificateUpdateForm) => {
    try {
      const rs = await updateSpecificSkillCertificateApi({
        specificSkillCertificates: [
          {
            id: toNumber(selectedSpecificSkillCertificate.id),
            certificateName: data.certificateName,
            certificateType: data.certificateType,
            description: data.description,
            specificSkillCertificateCompanySpecificIndustries: selectedCertificateIndustryRelationRows.map((item) => ({
              id: toNumber(item.id),
              companySpecificIndustryId: toNumber(item.companySpecificIndustryId),
              specificSkillCertificateId: toNumber(item.specificSkillCertificateId),
            })),
          },
        ],
      });
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GLOBAL.MASTER_SPECIFIC_SKILL_CERTIFICATE] });
      onClose();
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  return (
    <Modal width={800} title="編集" isOpen={isOpen} enableFooter={false} onClose={onClose} enableCloseIcon={false}>
      <Form methods={methods} onSubmit={onSubmit} schema={masterDataUpdateSpecificSkillCertificateSchema}>
        <Grid container spacing={3} py={3}>
          <Grid item xs={6}>
            <FormField control={control} name="certificateName" label="特定技能合格証">
              <Input />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="certificateType" label="1号or2号">
              <Select options={objectToOption(SPECIFIC_SKILL_CERTIFICATE_TYPE)} />
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <FormField control={control} name="description" label="説明">
              <Textarea minRows={3} maxRows={3} />
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <Stack gap={2}>
              <FormField>
                <FormLabel required>分野</FormLabel>
              </FormField>
              <CertificateIndustryRelationFormTable
                selectedCertificateIndustryRelationRows={selectedCertificateIndustryRelationRows}
                setSelectedCertificateIndustryRelationRows={setSelectedCertificateIndustryRelationRows}
                existingSpecificSkillCertificateId={selectedSpecificSkillCertificate?.id || 0}
                existingSpecificSkillCertificate={selectedSpecificSkillCertificate?.certificateName || EMPTY_STR.TEXT}
              />
            </Stack>
          </Grid>
        </Grid>
        <Stack flexDirection="row" justifyContent="center" gap="24px">
          <Button variant="outline" onClick={onClose}>
            キャンセル
          </Button>
          <Button type="submit">保存</Button>
        </Stack>
      </Form>
    </Modal>
  );
};
